import { Box, Button, Center, Group, Stack, Text } from "@mantine/core";
import React, { JSX, useEffect, useState } from "react";
import { completeLogin } from "../../hooks/ovokApi";

export interface TenantSelectionProps {
	sessionCode: string;
	codeVerifier: string;
	profiles: Array<{
		tenantCode: string;
		project: {
			id: string;
			resourceType: string;
			name: string;
		};
		profile: {
			resourceType: string;
			id: string;
			meta: {
				project: string;
				versionId: string;
				lastUpdated: string;
				author: { reference: string };
				compartment: Array<{ reference: string }>;
			};
			name: Array<{ given: string[]; family: string }>;
			telecom: Array<{ system: string; use: string; value: string }>;
		};
	}>;
	onSuccess: (response: {
		accessToken: string;
		refreshToken: string;
		project: { reference: string; display: string };
		profile: { reference: string; display: string };
		expiresIn: number;
	}) => void;
	onError: (error: string) => void;
}

export function TenantSelection({
	sessionCode,
	codeVerifier,
	profiles,
	onSuccess,
	onError,
}: TenantSelectionProps): JSX.Element {
	const [selectedProfile, setSelectedProfile] = useState<number>(0);

	useEffect(() => {
		// If there's only one profile, automatically select it
		if (profiles.length === 1) {
			handleProfileSelect(0);
		}
	}, [profiles]);

	const handleProfileSelect = async (index: number = selectedProfile) => {
		try {
			const selected = profiles[index];
			const response = await completeLogin(
				sessionCode,
				codeVerifier,
				selected.tenantCode
			);

			onSuccess(response);
		} catch (error) {
			onError("Failed to complete login. Please try again.");
		}
	};

	// If there's only one profile, show a loading state
	if (profiles.length === 1) {
		return (
			<Center>
				<Stack align="center" gap="md">
					<Text>Logging in...</Text>
				</Stack>
			</Center>
		);
	}

	return (
		<Stack w="100%" gap="xs">
			<Text mb="md" ta="center">
				Select a profile to continue
			</Text>

			{profiles.map((profile, index) => (
				<Box
					key={profile.tenantCode}
					p="md"
					style={{
						border:
							selectedProfile === index
								? "2px solid #7F56D9"
								: "1px solid #E2E8F0",
						borderRadius: "8px",
						cursor: "pointer",
					}}
					onClick={() => setSelectedProfile(index)}
				>
					<Text fw={500}>
						{profile.profile.name[0].given[0]} {profile.profile.name[0].family}
					</Text>
					<Text c="dimmed" size="sm">
						{profile.project.name}
					</Text>
					<Text c="dimmed" size="sm">
						{profile.profile.telecom[0].value}
					</Text>
				</Box>
			))}

			<Button mt="xl" type="submit" onClick={() => handleProfileSelect()}>
				Continue
			</Button>
		</Stack>
	);
}
